import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { ArrowIcon } from "../../../assets/icons/icon";
import { LanguageContext } from "../../../context/LanguageContext";
import { Box, capitalize } from "@mui/material";
import { AmountBox, AppAmountBoxWrapper } from "../../common";
import { AppPagination, ParagraphText } from "../../../Libraries";
import { FilterNonIdeal } from "../../common/NewNonIdeal";
import { useAuthContext } from "../../../context/AuthContext";
import {
  DEFAULT_DATE_FILTERS_EXPORT,
  ITEMS_PER_PAGE,
  NEW_SORT,
  SORT,
  TABLE_FILTERS_WITHOUT_CATEGORY,
  upperCase,
} from "../../../constants";
import { formatNum } from "../../../oscar-pos-core/constants";
import { useTransactionHistory } from "../../../hooks/useTransactionHistory";
import InvoiceRowItem from "./InvoiceRowItem";
import { bulkUpdateOrderId } from "../../../database/helpers/pos_txns";
import { useNavigate } from "react-router-dom";
import {
  buildFiltersConfig,
  dateFormatInNumberWithSpace,
} from "../../../utils";
import { analytics, triggerLogEvent } from "../../../config/firebase";
import AppTableFilters from "../../common/AppTableFilters";
import { useFiltersHandler } from "../../../hooks/useFilterHandler";
import useDownlaodPdf from "../../../hooks/useDownlaodPdf";
import { of } from "rxjs";
import withObservables from "@nozbe/with-observables";

const SortArrow = ({ isAscending, disabled }) => (
  <ArrowIcon
    id="sale_pos_arrow_sort"
    style={{
      ...(isAscending && { transform: "rotate(-0.5turn)" }),
      marginLeft: "4px",
      marginBottom: "4px",
      ...(disabled && { opacity: 0.3, pointerEvents: "none" }),
    }}
  />
);

export const InvListTableHeads = [
  {
    name: "Date",
    nameKey: "date",
    isArrow: true,
    onClick: (setDirection) => {
      setDirection((c) => !c);
    },
    showDataKey: "date",
    className: "textLeft sevenColumn",
    bodyClassName: "newFontColor",
  },
  {
    name: "Order no.",
    nameKey: "pos_order_no",
    showDataKey: "invoice_number",
    outName: "PO Number",
    className: "textLeft sevenColumn",
    bodyClassName: "newFontColor",
    headerClassName: "newLightColor",
  },
  {
    name: "Customer",
    nameKey: "customer",
    showDataKey: "cust_name",
    className: "textLeft sevenColumn",
    bodyClassName: "newFontColor",
    headerClassName: "newLightColor",
  },

  {
    name: "Status",
    nameKey: "status",
    showDataKey: "status",
    onClick: (setDirection) => {
      setDirection((c) => !c);
    },
    isArrow: true,
    className: "textLeft sevenColumn",
    bodyClassName: "newFontColor",
    headerClassName: "newLightColor",
  },
  {
    name: "Amount",
    nameKey: "amount",
    showDataKey: "amount",
    rsLabel: true,
    className: "textRight sevenColumn",
    bodyClassName: "newFontColor",
    headerClassName: "newLightColor",
  },
  {
    name: "Balance",
    nameKey: "balance",
    showDataKey: "balance",
    rsLabel: true,
    className: "textRight sevenColumn",
    bodyClassName: "newFontColor",
    headerClassName: "newLightColor fontSize14",
  },
  {
    name: "Actions",
    nameKey: "actions",
    showActionButton: true,
    className: "textCenter sevenColumn",
    bodyClassName: "newFontColor d-flex justify-content-center",
    headerClassName: "newLightColor",
  },
];

const SalePOSList = ({ customers, initialPosSale }) => {
  const { business, user } = useAuthContext();
  const [pageSize, setPageSize] = useState(0);
  const [I18n] = useContext(LanguageContext);
  const navigate = useNavigate();
  const { onDownload, downloadLoading } = useDownlaodPdf(user);
  const [selectedContactId, setSelectedContactId] = useState("");
  const justClosedDropdownRef = useRef(false);

  const {
    filters,
    appliedChips,
    handleFilterChange,
    resetFilters,
    handleRemoveChip,
    isResetFilter,
    setIsResetFilter,
  } = useFiltersHandler(undefined, I18n);

  useEffect(() => {
    handleUpdateTrans();
  }, []);
  const handleUpdateTrans = async () => {
    await bulkUpdateOrderId(business.id);
  };

  const {
    list,
    totalFilterCount,
    totalPaid,
    totalUnPaid,
    handleTransaction,
    isLoading,
    totalSaleAmount,
  } = useTransactionHistory(initialPosSale);

  useEffect(() => {
    setPageSize(0);
    handleTransaction({
      business,
      filters,
      limit: ITEMS_PER_PAGE,
      offset: 0,
    });
  }, [filters]);

  const transactionData = useMemo(() => {
    let MappedInvList = list?.map((transaction) => {
      return {
        id: transaction?.id,
        key: transaction?.id,
        date: dateFormatInNumberWithSpace(transaction?.transacted_at),
        invoice_number: transaction?.invoice_number
          ? `${upperCase(transaction?.invoice_prefix + transaction?.invoice_number).replace("#", " #")}`
          : "-",
        amount: `${transaction?.status?.toLowerCase() == "paid" ? "+" : ""}Rs. ${Number(transaction?.amount).toFixed(2)}`,
        // display only; logic reads transaction.status. Stored values are English, so translate the label
        status: `${capitalize(I18n[String(transaction?.status).toLowerCase()] || transaction?.status)} / ${capitalize(transaction?.method ? I18n[String(transaction.method).toLowerCase()] || transaction.method : I18n.cash || "Cash")} `,
        paymentMethod: transaction?.method,
        balance:
          transaction?.status?.toLowerCase() == "paid"
            ? `Rs. 0`
            : `Rs. ${formatNum(transaction?.amount - transaction?.advance_amount)}`,
      };
    });
    return MappedInvList;
  }, [list, I18n]);

  const invoiceStatusList = [
    {
      title: I18n.total_sales,
      id: "total_sales",
      amount: formatNum(totalSaleAmount),
      rsLabel: true,
    },

    {
      amount: formatNum(totalPaid),
      title: I18n.paid,
      id: "paid",
      rsLabel: true,
    },

    {
      title: I18n.unpaid,
      id: "unpaid",
      amount: formatNum(totalUnPaid),
      rsLabel: true,
      amountParaTextClassname: "foundationOrangeColor",
    },
  ];

  const handleDownloadReport = (startDate, endDate) => {
    triggerLogEvent(analytics, "sale_pos_report_download");
    let paramsForReport = {
      name: "invoice_statement.pdf",
      template_name: "sale_report.hbs",
      method : "all",
      business_id: business.id,
      start_date: startDate || "",
      end_date: endDate || "",
      pdf: true,
    };

    onDownload(paramsForReport, () => {}, navigate);
  };

  return (
    <Box className={"AccountWrapper"} height={"100%"}>
      <AppTableFilters
        filters={TABLE_FILTERS_WITHOUT_CATEGORY}
        onFilterChange={handleFilterChange}
        appliedChips={appliedChips}
        filterState={filters}
        onRemoveChip={(chip) => handleRemoveChip(chip)}
        filtersConfig={buildFiltersConfig(TABLE_FILTERS_WITHOUT_CATEGORY, [], null, null, I18n.search_by_contact_or_order_no || "Search by contact or order no", null, null, null, null, I18n)}
        isResetFilter={isResetFilter}
        setIsResetFilter={setIsResetFilter}
        exportConfig={{
          onExport: handleDownloadReport,
          loading: downloadLoading,
          dateFilters: DEFAULT_DATE_FILTERS_EXPORT,
          applyBtnText: I18n.export,
          spinnerColor: "black",
        }}
      />

      <AppAmountBoxWrapper>
        <AmountBox
          isNonIdeal={!transactionData.length}
          data={invoiceStatusList}
        />
      </AppAmountBoxWrapper>

      <div className="invoiceListTableHeader saleListingRow">
        {InvListTableHeads.map((headInfo, ind) => (
          <div className={`invListTableHeaderCol ${headInfo?.className}`}>
            {
              <ParagraphText
                className={`fontSize12 fontWeight500 newLightColor ${
                  headInfo?.onClick && transactionData.length > 0
                    ? "cursorPointer"
                    : ""
                }`}
                key={headInfo.name}
                onClick={() => {
                  if (!transactionData.length) return;
                  if (headInfo.showDataKey === "date") {
                    handleFilterChange(
                      "sort",
                      filters.sort === NEW_SORT.Oldest
                        ? NEW_SORT.Newest
                        : NEW_SORT.Oldest,
                    );
                  }

                  if (headInfo.showDataKey === "status") {
                    handleFilterChange(
                      "sort",
                      filters.sort === NEW_SORT.unpaid
                        ? NEW_SORT.paid
                        : NEW_SORT.unpaid,
                    );
                  }
                }}
              >
                {I18n[headInfo.nameKey] || headInfo.name}
                {headInfo.showDataKey === "date" && (
                  <SortArrow
                    isAscending={filters.sort === NEW_SORT.Oldest}
                    disabled={!transactionData.length}
                  />
                )}

                {headInfo.showDataKey === "status" && (
                  <SortArrow
                    isAscending={filters.sort === NEW_SORT.unpaid}
                    disabled={!transactionData.length}
                  />
                )}
              </ParagraphText>
            }
          </div>
        ))}
      </div>

      <Box
        className={`accountListBodyWarpper generic-scrollbar`}
        id="scrollableDiv"
      >
        {transactionData?.length > 0 ? (
          <AppPagination
            data={{
              handleNext: (e) => {
                if (transactionData.length < totalFilterCount) {
                  setPageSize(pageSize + 1);
                  handleTransaction({
                    business,
                    filters,
                    limit: ITEMS_PER_PAGE,
                    offset: (pageSize + 1) * ITEMS_PER_PAGE,
                    isCalculateTotal: false,
                  });
                }
              },
              pageLoad: isLoading,
              count: totalFilterCount,
              list: transactionData,
              scrollableTarget: "scrollableDiv",
            }}
          >
            {transactionData?.map((item, ind) => {
              const getInvoicebyID = list.find((val) => val.id === item.id);
              return (
                <InvoiceRowItem
                  index={ind}
                  key={item.id}
                  transaction={getInvoicebyID}
                  detail={item}
                  selectedContactId={selectedContactId}
                  setSelectedContactId={setSelectedContactId}
                  justClosedDropdownRef={justClosedDropdownRef}
                  customers={customers}
                  refetchList={() => {
                    setPageSize(0);
                    handleTransaction({
                      business,
                      filters,
                      limit: ITEMS_PER_PAGE,
                      offset: 0,
                    });
                  }}
                />
              );
            })}
          </AppPagination>
        ) : !isLoading ? (
          <Box className="h-100">
            <FilterNonIdeal
              title={I18n.no_transactions_matching_those_filters}
              subTitle={I18n.try_adjusting_filters}
              subTitleTextOnClick={resetFilters}
              subTitleText={I18n.reset_small}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

const enhance = withObservables(["business"], ({ business }) => {
  return {
    customers: business ? business?.getAllCustomers.observe() : of([]),
    initialPosSale: business
      ? business.getPOSReport(undefined, undefined, "").observe()
      : of([]),
  };
});

export default enhance(SalePOSList);
